import { nanoid } from "@reduxjs/toolkit";
import classNames from "classnames";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ReservationListInterface } from "../../../../../store/slices/reservationSlice";
import { useTypedSelector } from "../../../../../store/store";
import { BOOKING, routeParams } from "../../../../../utils/constants/routes";
import SvgIcon from "../../../../shared/SvgIcon";
import styles from "../../index.module.scss";

type SortType = "passenger_name" | "embarkation_date" | "booking_date";

interface ContentProps {
  bookingList: ReservationListInterface[];
  sortBookingList: (type: SortType) => void;
}

function Content({ bookingList, sortBookingList }: ContentProps) {
  const { t } = useTranslation();

  const { date_format, pnr_column_name, cruise_column_name } = useTypedSelector(
    (state) => state.environment,
  );

  return (
    <div className={styles.fieldsContainer}>
      <div className={styles.headerRow}>
        <div
          className={classNames(styles.column, styles.column_md)}
          style={{ cursor: "pointer" }}
          onClick={() => sortBookingList("booking_date")}>
          <span className={styles.uppercase}>{t("booked date")}</span>
          <div className={styles.svgBlock}>
            <SvgIcon className={styles.arrowUp} type="FiTriangle" />
            <SvgIcon className={styles.arrowDown} type="FiTriangle" />
          </div>
        </div>

        <div className={classNames(styles.column, styles.column_sm)}>
          <span className={styles.uppercase}>{pnr_column_name}</span>
        </div>
        <div
          className={classNames(styles.column, styles.column_lg)}
          style={{ cursor: "pointer" }}
          onClick={() => sortBookingList("passenger_name")}>
          <span className={styles.uppercase}>{t("passenger name")}</span>
          <div className={styles.svgBlock}>
            <SvgIcon className={styles.arrowUp} type="FiTriangle" />
            <SvgIcon className={styles.arrowDown} type="FiTriangle" />
          </div>
        </div>
        <div
          className={classNames(styles.column, styles.column_md)}
          style={{ cursor: "pointer" }}
          onClick={() => sortBookingList("embarkation_date")}>
          <span className={styles.uppercase}>{t("embark date")}</span>
          <div className={styles.svgBlock}>
            <SvgIcon className={styles.arrowUp} type="FiTriangle" />
            <SvgIcon className={styles.arrowDown} type="FiTriangle" />
          </div>
        </div>
        <div className={classNames(styles.column, styles.column_lg)}>
          <span className={styles.uppercase}>{cruise_column_name}</span>
        </div>
        <div className={styles.actionColumn} />
      </div>
      {bookingList.map((booking) => {
        return (
          <div key={nanoid()} className={styles.contentRow}>
            <div className={classNames(styles.column, styles.column_md)}>
              <span>{dayjs(booking.created).format(date_format)}</span>
            </div>

            <div className={classNames(styles.column, styles.column_sm)}>
              <span>{booking.pnr}</span>
            </div>

            <div className={classNames(styles.column, styles.column_lg)}>
              <span>{booking.lead_lastname}</span>
            </div>

            <div className={classNames(styles.column, styles.column_md)}>
              <span>{dayjs(booking.embarkation_date).format(date_format)}</span>
            </div>

            <div className={classNames(styles.column, styles.column_lg)}>
              <span>{booking.cruise_name}</span>
            </div>

            <div className={styles.actionColumn}>
              <Link to={routeParams(BOOKING, [["pnr", booking.pnr]])}>
                <SvgIcon className={styles.svgAction} type="FiExternalLink" />
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Content;
